import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone, Share, Plus } from 'lucide-react';

export const InstallAppBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    // Check if already installed as PWA
    const isInstalled = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone === true;
    
    if (isInstalled) {
      console.log('App already installed');
      return;
    }

    // Check if dismissed recently
    const dismissedTime = localStorage.getItem('pwa_banner_dismissed');
    const oneDay = 24 * 60 * 60 * 1000;
    if (dismissedTime && (Date.now() - parseInt(dismissedTime)) < oneDay) {
      return;
    }

    // Detect iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    setIsIOS(isIOSDevice);

    // For Android/Chrome - capture the install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      console.log('Install prompt captured!');
      setDeferredPrompt(e);
      // Show banner after 2 seconds
      setTimeout(() => setShowBanner(true), 2000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // For iOS Safari - show banner with instructions
    if (isIOSDevice && isSafari) {
      setTimeout(() => setShowBanner(true), 2000);
    }

    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      console.log('App installed successfully!');
      setShowBanner(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      // Show iOS instructions
      setShowIOSInstructions(true);
      return;
    }

    if (deferredPrompt) {
      setIsInstalling(true);
      
      // Show the native install prompt
      deferredPrompt.prompt();
      
      // Wait for the user's response
      const { outcome } = await deferredPrompt.userChoice;
      console.log('User choice:', outcome);
      
      if (outcome === 'accepted') {
        console.log('User accepted install');
        setShowBanner(false);
      }
      
      setDeferredPrompt(null);
      setIsInstalling(false);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setShowIOSInstructions(false);
    localStorage.setItem('pwa_banner_dismissed', Date.now().toString());
  };

  if (!showBanner) return null;

  // iOS Instructions Modal
  if (showIOSInstructions) {
    return (
      <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in">
        <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md p-6 shadow-2xl animate-slide-up">
          <div className="flex justify-between items-start mb-5">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-amber-500 to-orange-500 p-3 rounded-2xl shadow-lg">
                <Smartphone className="text-white" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-lg">Agregar a Inicio</h3>
                <p className="text-gray-500 text-sm">Solo 3 pasos</p>
              </div>
            </div>
            <button onClick={handleDismiss} className="p-2 hover:bg-gray-100 rounded-full">
              <X size={20} className="text-gray-400" />
            </button>
          </div>
          
          <ol className="space-y-4">
            <li className="flex items-start gap-4 p-3 bg-gray-50 rounded-xl">
              <span className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-amber-500 to-orange-500 text-white rounded-full flex items-center justify-center font-bold">1</span>
              <div className="flex-1">
                <p className="text-gray-900 font-medium">Tocá el botón Compartir</p>
                <p className="text-gray-500 text-sm mt-1 flex items-center gap-2">
                  Buscá este ícono: <Share size={18} className="text-blue-500" />
                </p>
              </div>
            </li>
            <li className="flex items-start gap-4 p-3 bg-gray-50 rounded-xl">
              <span className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-amber-500 to-orange-500 text-white rounded-full flex items-center justify-center font-bold">2</span>
              <div className="flex-1">
                <p className="text-gray-900 font-medium">Deslizá y buscá</p>
                <p className="text-gray-500 text-sm mt-1 flex items-center gap-2">
                  <Plus size={16} className="text-gray-600 bg-gray-200 rounded p-0.5" />
                  <span>"Agregar a pantalla de inicio"</span>
                </p>
              </div>
            </li>
            <li className="flex items-start gap-4 p-3 bg-gray-50 rounded-xl">
              <span className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-amber-500 to-orange-500 text-white rounded-full flex items-center justify-center font-bold">3</span>
              <div className="flex-1">
                <p className="text-gray-900 font-medium">Confirmá tocando "Agregar"</p>
                <p className="text-gray-500 text-sm mt-1">¡Listo! La app aparecerá en tu inicio</p>
              </div>
            </li>
          </ol>

          <button
            onClick={handleDismiss}
            className="w-full mt-6 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-2xl font-bold text-lg shadow-lg shadow-amber-500/30"
          >
            ¡Entendido!
          </button>
        </div>
      </div>
    );
  }

  // Main Install Banner
  return (
    <div className="fixed bottom-24 left-3 right-3 z-[60] animate-slide-up">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden max-w-md mx-auto">
        {/* Header accent */}
        <div className="h-1 bg-gradient-to-r from-amber-500 to-orange-500" />
        
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 bg-gradient-to-br from-amber-500 to-orange-500 p-3 rounded-xl shadow-lg shadow-amber-500/30">
              <Download className="text-white" size={24} />
            </div>
            
            <div className="flex-1 min-w-0">
              <h4 className="font-bold text-gray-900">Instalá la App</h4>
              <p className="text-sm text-gray-500 mt-0.5">
                Accedé más rápido y leé sin conexión
              </p>
            </div>

            <button
              onClick={handleDismiss}
              className="flex-shrink-0 p-1.5 hover:bg-gray-100 rounded-full"
            >
              <X size={18} className="text-gray-400" />
            </button>
          </div>

          <div className="flex gap-3 mt-4">
            <button
              onClick={handleDismiss}
              className="flex-1 py-3 text-sm font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
            >
              Ahora no
            </button>
            <button
              onClick={handleInstallClick}
              disabled={isInstalling}
              className="flex-1 py-3 text-sm font-bold text-white bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl shadow-md shadow-amber-500/30 hover:from-amber-600 hover:to-orange-600 transition-all disabled:opacity-70 flex items-center justify-center gap-2"
            >
              {isInstalling ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Instalando...
                </>
              ) : (
                <>
                  <Download size={18} />
                  Instalar
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-slide-up { animation: slide-up 0.4s ease-out; }
        .animate-fade-in { animation: fade-in 0.3s ease-out; }
      `}</style>
    </div>
  );
};
